import { isAccess, isResourceAccess } from '@css-xp/base';
import {
  BillingFrequency,
  BillingStatus,
  EntitlementDetails,
  ProvisioningStatus,
} from '@css-xp/graphtypes/src/generated-types-new';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { Permissions, Resources } from '@atlassian/cssxp-typescommon';
import {
  isAtlasProduct,
  isConfluenceProduct,
  isJiraProduct,
  isJPDProduct,
  isNpfProduct,
} from '@atlassian/cssxp-utils-product';
import isEmpty from 'lodash/isEmpty';
import lowerCase from 'lodash/lowerCase';
import { isTrialEntitlement } from '../../entitlement-page-utility/entitlement-summary/entitlment-summary-utils';
import { validateFreeSubscriptions } from '../../entitlement-page';
import { PREDICATES } from '../constants';
import {
  isDeletionPendingEntitlement,
  isOfferingArchived,
  isOfferingAtNotice,
  isAnnualTermedSubscription,
} from '@atlassian/cssxp-utils-entitlement';
import { PricingPlanCycle } from '@atlassian/cssxp-ccp-types';

interface FeatureFlags {
  isAtlasProdEnable?: boolean;
  isSandboxEntEnable?: boolean;
  isJPDEnable?: boolean;
  isAnnualTermEnabled?: boolean;
}

export const getPredicates = (
  entitlement: EntitlementDetails | undefined,
  type: string | undefined,
  featureFlags?: FeatureFlags
) => {
  if (!type || isEmpty(entitlement)) {
    return false;
  }

  const {
    billingStatus,
    billingFrequency,
    availablePromotions,
    promotions,
    subscriptionId,
    parentSen,
    provisioningStatus,
    baseProduct,
  } = entitlement as EntitlementDetails;
  const isPromotionsAvailable = () => {
    // Using `!!` to avoid null return (typecheck)
    return !!((availablePromotions && availablePromotions.length > 0) || (promotions && promotions.length > 0));
  };
  const isParentSenAvailable = () => !!parentSen;
  const isActive = lowerCase(billingStatus ?? '') === lowerCase(BillingStatus.ACTIVE);
  const isInactive = lowerCase(billingStatus ?? '') === lowerCase(BillingStatus.INACTIVE);
  const isNotAnnual = lowerCase(billingFrequency ?? '') !== lowerCase(BillingFrequency.ANNUAL);
  const isNotBillableEntitlement = !!(subscriptionId && billingFrequency === null);
  const isStatusPage = baseProduct === EntitlementBaseProduct.STATUS_PAGE;
  const isTrial = isTrialEntitlement(entitlement as EntitlementDetails, isStatusPage);
  const isAnnualTermEnabled = featureFlags?.isAnnualTermEnabled ?? false;

  const hasScheduledChanges = () => {
    return !!entitlement?.scheduledChanges;
  };

  const checkForNpfProduct = () => {
    return isNpfProduct(entitlement?.productId);
  };

  const checkForJiraProduct = () => {
    return isJiraProduct(entitlement?.productId);
  };

  const checkForConfluenceProduct = () => {
    return isConfluenceProduct(entitlement?.productId);
  };

  const checkForAtlasProduct = () => {
    return isAtlasProduct(entitlement?.productId);
  };

  const checkForJPDProduct = () => {
    return isJPDProduct(entitlement?.productId);
  };

  const canHaveOptOutOption = () => {
    if (checkForJiraProduct() || checkForConfluenceProduct() || checkForAtlasProduct() || checkForJPDProduct()) {
      if (entitlement?.upgradeTrial) return true;
      if (entitlement?.edition?.includes('Free') || entitlement?.edition?.includes('Standard')) {
        return false;
      } else return true;
    } else return false;
  };

  const isFreeProduct = validateFreeSubscriptions(entitlement as EntitlementDetails);
  const checkForSandboxEntitlement = () => {
    return featureFlags?.isSandboxEntEnable && entitlement?.isSandbox ? true : false;
  };

  const commonPredicateForCancelOrReactivate =
    isResourceAccess(Resources.ENTITLEMENT_ALL) && isAccess(Permissions.SUBSCRIPTION_ENTITLEMENT_MANAGE);

  const deleteProduct = commonPredicateForCancelOrReactivate && isAccess(Permissions.ENTITLEMENT_PRODUCT_DELETE);

  const availableAddOns = entitlement?.offering?.availableAddOns;
  const hasAvailableAddOns = !!(availableAddOns && availableAddOns.length > 0);
  const isAppArchived = entitlement?.isApp && isOfferingArchived(entitlement);
  const isAppArchivedOrAtNotice =
    entitlement?.isApp && (isOfferingArchived(entitlement) || isOfferingAtNotice(entitlement));

  switch (type) {
    case PREDICATES.MANAGE_SUBSCRIPTION:
      return (
        isActive &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_WRITE)
      );

    case PREDICATES.CANCEL_OR_REACTIVATE_ENTITLEMENT:
      return isActive
        ? commonPredicateForCancelOrReactivate
        : commonPredicateForCancelOrReactivate && !isAppArchivedOrAtNotice;

    case PREDICATES.DELETE_PRODUCT:
      return (
        deleteProduct &&
        isInactive &&
        provisioningStatus === ProvisioningStatus.SUSPENDED &&
        !checkForSandboxEntitlement() &&
        !isDeletionPendingEntitlement(entitlement) &&
        !isAppArchived
      );

    case PREDICATES.CANCEL_SCHEDULED_SUBSCRIPTION:
      return (
        isActive &&
        hasScheduledChanges() &&
        !isParentSenAvailable() &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_BILLING_PERIOD_WRITE) &&
        !isAppArchived
      );

    case PREDICATES.OPT_OUT:
      return (
        isActive &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_ENTITLEMENT_MANAGE) &&
        canHaveOptOutOption() &&
        !checkForSandboxEntitlement() &&
        (entitlement?.upgradeTrial ?? entitlement?.isOptOutAllowed ?? false) &&
        !isAppArchived
      );

    case PREDICATES.SET_BILL_DATE:
      return (
        isActive &&
        !isFreeProduct &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        !isAnnualTermedSubscription(entitlement as EntitlementDetails, isAnnualTermEnabled) &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_ENTITLEMENT_MANAGE) &&
        !isAppArchived
      );

    case PREDICATES.SWITCH_TO_ANNUAL:
      return (
        isActive &&
        isNotAnnual &&
        !isParentSenAvailable() &&
        !checkForNpfProduct() &&
        !checkForJiraProduct() &&
        !checkForConfluenceProduct() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_BILLING_PERIOD_WRITE) &&
        !checkForAtlasProduct() &&
        !checkForJPDProduct() &&
        !checkForSandboxEntitlement() &&
        !isAppArchived
      );

    case PREDICATES.SWITCH_TO_MONTHLY:
      return (
        isActive &&
        !isNotAnnual &&
        !isParentSenAvailable() &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_BILLING_PERIOD_WRITE) &&
        !isAppArchived
      );

    case PREDICATES.UPDATE_INVOICE_GROUP:
      return (
        isActive &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.INVOICE_GROUP_WRITE)
      );

    case PREDICATES.TRANSFER_BILLING_ACCOUNT:
      return (
        isActive &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.TRANSACTION_ACCOUNT_WRITE)
      );

    case PREDICATES.REWIND_SUBSCRIPTION:
      return (
        isActive &&
        !entitlement?.isApp &&
        entitlement?.billingFrequency !== PricingPlanCycle.ANNUAL &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_ENTITLEMENT_MANAGE)
      );

    case PREDICATES.REBILL:
      return (
        isActive &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_ENTITLEMENT_MANAGE)
      );

    case PREDICATES.CHANGE_PRICING:
      return (
        isActive &&
        !isParentSenAvailable() &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_PRICING_PLAN_WRITE)
      );

    case PREDICATES.ADD_PROMOTIONS:
      return (
        isActive &&
        isPromotionsAvailable() &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_PROMOTION_WRITE) &&
        !isAppArchived
      );

    case PREDICATES.REMOVE_PROMOTION:
      return (
        isActive &&
        !isNotBillableEntitlement &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_PROMOTION_WRITE) &&
        !isAppArchived
      );

    case PREDICATES.EXTEND_TRIAL:
      return (
        isActive &&
        isTrial &&
        isNotBillableEntitlement &&
        !isParentSenAvailable() &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_TRAIL_LENGTH_DAYS_WRITE) &&
        !isAppArchivedOrAtNotice
      );

    case PREDICATES.SUBSCRIBE_TO_ADD_ONS:
      return (
        hasAvailableAddOns &&
        isActive &&
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        isResourceAccess(Resources.ENTITLEMENT_ALL) &&
        isAccess(Permissions.SUBSCRIPTION_WRITE) &&
        !isAppArchived
      );

    case PREDICATES.CREATE_QUOTE:
      return (
        !checkForNpfProduct() &&
        !checkForSandboxEntitlement() &&
        !isAppArchivedOrAtNotice &&
        !(provisioningStatus === ProvisioningStatus.SUSPENDED || provisioningStatus === ProvisioningStatus.DEACTIVATED)
      );

    default:
      return false;
  }
};
